/**
 * Controls — bottom-bar mic, camera, and leave buttons (T079 / US3, T087 / US4).
 *
 * Reads localMediaState from the zustand store to reflect the current
 * on/off state. Calls onToggleMic / onToggleCam on click, which wire
 * through Room.tsx to RoomClient.setMicEnabled / setCamEnabled.
 * onLeave wires to RoomClient.leave() + navigate('/').
 *
 * Accessibility: real <button> elements with an action-describing aria-label.
 * Large hit targets (44 × 44 px minimum) for touch friendliness.
 */
import { useTranslation } from 'react-i18next'
import type { PeerMediaState } from '../room/store.js'
import { canSwitchCamera } from '../room/camera.js'
import { LayoutToggle } from './LayoutToggle.js'
import { CameraSwitchButton } from './CameraSwitchButton.js'
import styles from './Controls.module.css'

interface ControlsProps {
  localMediaState: PeerMediaState
  onToggleMic: () => void
  onToggleCam: () => void
  onLeave: () => void
  /** Number of remote peers — toggle is shown only when ≥ 1 (FR-002 / C1). */
  remotePeerCount: number
  /** Number of video-input devices — switch button shown when ≥ 2 and cam is on. */
  videoInputCount: number
  /** Called when the user activates the switch-camera control. */
  onSwitchCamera: () => void
}

export function Controls({ localMediaState, onToggleMic, onToggleCam, onLeave, remotePeerCount, videoInputCount, onSwitchCamera }: ControlsProps) {
  const { t } = useTranslation()
  const micOn = localMediaState.mic
  const camOn = localMediaState.cam

  const showLayoutToggle = remotePeerCount >= 1
  const showCameraSwitch = canSwitchCamera(videoInputCount, camOn)

  return (
    <div className={styles.bar} role="toolbar" aria-label={t('controls.toolbar')}>
      <button
        data-testid="control-mic"
        className={`${styles.btn} ${micOn ? styles.btnOn : styles.btnOff}`}
        aria-label={micOn ? t('controls.muteMic') : t('controls.unmuteMic')}
        onClick={onToggleMic}
        type="button"
      >
        {micOn ? (
          <MicOnIcon />
        ) : (
          <MicOffIcon />
        )}
        <span className={styles.btnLabel}>
          {micOn ? t('controls.muteMic') : t('controls.unmuteMic')}
        </span>
      </button>

      <button
        data-testid="control-cam"
        className={`${styles.btn} ${camOn ? styles.btnOn : styles.btnOff}`}
        aria-label={camOn ? t('controls.stopCam') : t('controls.startCam')}
        onClick={onToggleCam}
        type="button"
      >
        {camOn ? (
          <CamOnIcon />
        ) : (
          <CamOffIcon />
        )}
        <span className={styles.btnLabel}>
          {camOn ? t('controls.stopCam') : t('controls.startCam')}
        </span>
      </button>

      {showLayoutToggle && <LayoutToggle />}

      {showCameraSwitch && <CameraSwitchButton onSwitchCamera={onSwitchCamera} />}

      <button
        data-testid="control-leave"
        className={`${styles.btn} ${styles.btnLeave}`}
        aria-label={t('controls.leave')}
        onClick={onLeave}
        type="button"
      >
        <LeaveIcon />
        <span className={styles.btnLabel}>{t('controls.leave')}</span>
      </button>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Inline SVG icons — no external dependency needed
// ---------------------------------------------------------------------------

function MicOnIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
      <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
      <line x1="12" y1="19" x2="12" y2="23" />
      <line x1="8" y1="23" x2="16" y2="23" />
    </svg>
  )
}

function MicOffIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <line x1="1" y1="1" x2="23" y2="23" />
      <path d="M9 9v3a3 3 0 0 0 5.12 2.12M15 9.34V4a3 3 0 0 0-5.94-.6" />
      <path d="M17 16.95A7 7 0 0 1 5 12v-2m14 0v2a7 7 0 0 1-.11 1.23" />
      <line x1="12" y1="19" x2="12" y2="23" />
      <line x1="8" y1="23" x2="16" y2="23" />
    </svg>
  )
}

function CamOnIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <polygon points="23 7 16 12 23 17 23 7" />
      <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
    </svg>
  )
}

function CamOffIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M16 16v1a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h2m5.66 0H14a2 2 0 0 1 2 2v3.34l1 1L23 7v10" />
      <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
  )
}

function LeaveIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <polyline points="16 17 21 12 16 7" />
      <line x1="21" y1="12" x2="9" y2="12" />
    </svg>
  )
}
